import React, { useRef, useEffect, useState, useContext } from "react";
import ExportLayersWidget from "../export-layers/ExportLayersWidget";
import SpatialSelectWidget from "./spatial-select/SpatialSelectWidget";
import useTablesDataHook from "../table-panels/useTablesDataHook";
import { ISelectionDataModel } from "../../../hooks/context/SelectionDataContext";
import {
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import SelectionFormButtons from "./selection-buttons/SelectionFormButtons";
import {
  defaultAllVisibleLayers,
  replaceMissingElements,
  returnFormType,
} from "./attribute-select/AttributeSelectVM";
import AttributeQueryBlock from "./attribute-select/AttributeQueryBlock";
import { ShowReportsContext } from "../../../hooks/context/ShowReportsContext";
import { ShowMapContext } from "../../../hooks/context/ShowMapContext";
import OpenReports from "./OpenReports";
import SelectCustomToolbar from "./custom-toolbar/SelectCustomToolbar";


const SelectFormWidget = ({
  activeWidget,
  view,
  portal,
  pageConfig,
  setHorizPanelHeight,
  tableSplit,
  setTableSplit,
  resetTabArray,
}) => {
  const [visible, setVisible] = useState(false);
  const [disable, setDisable] = useState(true);
  const [attributeObject, setAttributeObject] = useState({
    layerId: "",
    queryString: "",
  });
  const selectFormWidgetDiv = useRef(null);
  const [showExportLayersWidget, setShowExportLayersWidget] = useState("");
  const [currentLayerId, setCurrentLayerId] = useState<string>(defaultAllVisibleLayers);
  const [layerNameList, setLayerNameList] = useState([]);
  const [formFields, setFormFields] = useState({});
  const [fieldConfig, setFieldConfig] = useState([]);
  const [attributeQueryBlocks, setAttributeQueryBlocks] = useState([]);
  const [showSpatialQueryBlock, setShowSpatialQueryBlock] = useState(false);

  const showReportsCtx: any = useContext(ShowReportsContext);
  const showMapCtx:any=useContext(ShowMapContext);

  const payload: ISelectionDataModel | any = useTablesDataHook();

  useEffect(() => {
    if (payload.length) {
      if (tableSplit !== 2) {
        setTableSplit(2);
      }
    }
  }, [payload]);

  useEffect(() => {
    const layertitles = view.map.allLayers
      .filter((layer) => layer.type === "feature")
      .toArray()
      .map((lyr) => {
        return { title: lyr.title, id: lyr.id };
      })
      .sort((a, b) => {
        return a.title.localeCompare(b.title);
      });
    setLayerNameList(layertitles);
  }, [view]);

  useEffect(() => {
    setAttributeQueryBlocks([]);
    if (currentLayerId.length && currentLayerId !== defaultAllVisibleLayers) {
      let fields: any[] = [];
      let subTypeField;
      const selectedLayer = view.map.allLayers
        .filter((layer) => layer.id === currentLayerId)
        .getItemAt(0);
      selectedLayer?.popupTemplate.fieldInfos.forEach((field: any) => {
        if (field.visible) {
          fields.push({
            fieldName: field.fieldName,
            fieldLabel: field.label,
          });
        }
      });
      let fieldFormObject = {};
      fields.forEach((field) => {
        selectedLayer.fields.forEach((layerField) => {
          if (field.fieldName === layerField.name) {
            if (layerField.type === "date") {
              fieldFormObject[field.fieldName] = {
                fieldName: field.fieldName,
                queryValue: { begin: "", end: "" },
                queryOperator: "is",
                show: false,
              };
              field.type = layerField.type;
            } else {
              fieldFormObject[field.fieldName] = {
                fieldName: field.fieldName,
                queryValue: "",
                queryOperator: "is",
                show: false,
              };
              field.type = layerField.type;
            }

            if (layerField?.domain) {
              field.domains = [layerField.domain];
            } else {
              field.domains = [];
            }
          }
        });
        if (field.fieldName === selectedLayer?.subtypeField) {
          field.types = selectedLayer.types;
          field.subTypeField = true;
          subTypeField = field;
        } else {
          field.types = [];
          field.subTypeField = false;
        }
      });
      if (subTypeField) {
        subTypeField.types.forEach((type) => {
          fields.forEach((f) => {
            if (type.domains[f.fieldName]) {
              if (type.domains[f.fieldName].type === "coded-value") {
                f.domains.push(type.domains[f.fieldName]);
              }
            }
          });
        });
      }
      setFormFields(fieldFormObject);
      setFieldConfig(fields);
    } else {
      setFormFields({});
      setFieldConfig([]);
    }
  }, [currentLayerId, view]);

  useEffect(() => {
    const stringList = [];
    Object.entries(formFields).forEach((item: any) => {
      let matchFieldList = fieldConfig.filter((field) => {
        return field.fieldName === item[0] && item[1].queryValue;
      });
      if (matchFieldList.length > 0) {
        let valueType = returnFormType(matchFieldList[0].type);
        const operator = item[1].queryOperator === "is not" ? "<>" : "="
        if (item[1].queryValue && valueType === "text") {
          stringList.push(`${item[0]} ${operator} '${item[1].queryValue}'`);
        }
        if (item[1] && valueType === "number") {
          stringList.push(`${item[0]} ${operator} ${item[1].queryValue}`);
        }
        if (valueType === "date") {
          if (
            item[1].queryValue.begin.length &&
            item[1].queryValue.end.length
          ) {
            stringList.push(
              `${item[0]} BETWEEN DATE '${item[1].queryValue.begin}' AND DATE '${item[1].queryValue.end}'`
            );
          }
          if (
            item[1].queryValue.begin.length &&
            !item[1].queryValue.end.length
          ) {
            stringList.push(`${item[0]} >= DATE '${item[1].queryValue.begin}'`);
          }
          if (
            !item[1].queryValue.begin.length &&
            item[1].queryValue.end.length
          ) {
            stringList.push(`${item[0]} <= DATE '${item[1].queryValue.end}'`);
          }
        }
      }
    });
    let queryObject = {
      layerId: currentLayerId===defaultAllVisibleLayers?"":currentLayerId,
      queryString: stringList.join(" AND "),
    };
    setAttributeObject(queryObject);
  }, [formFields, fieldConfig, setAttributeObject, currentLayerId]);

  useEffect(() => {
    let activeFieldNames = [];
    Object.keys(formFields).forEach((key) => {
      if (formFields[key].show) {
        activeFieldNames.push(key);
      }
    });
    setAttributeQueryBlocks((prev) => {
      const oldArray = [...prev];
      const newArray = replaceMissingElements(oldArray, activeFieldNames);
      return newArray;
    });
  }, [formFields]);

  useEffect(() => {
    if (activeWidget === "select") {
      setVisible(true);
      setShowExportLayersWidget("exportTool");
    } else {
      setVisible(false);
      setShowExportLayersWidget("");
    }
  }, [activeWidget]);
  const layerDropdown = layerNameList.map((lyrObject) => {
    return (
      <MenuItem
        value={lyrObject.id}
        key={`lyrMenuItem-${lyrObject.title}-${lyrObject.id}`}
      >
        {lyrObject.title}
      </MenuItem>
    );
  });

  const fieldDropdown = fieldConfig.map((field) => {
    return (
      <MenuItem value={field.fieldName} key={`${field.fieldName}-field`}>
        {field.fieldLabel}
      </MenuItem>
    );
  });
  let attributeQueries;
  if (fieldConfig.length) {
    attributeQueries = attributeQueryBlocks.map((name) => {
      const field = fieldConfig.filter((f) => f.fieldName === name)[0];
      return (
        <div key={`att-query-block-${field?.fieldName}`}>
          <AttributeQueryBlock
            field={field}
            formFields={formFields}
            view={view}
            callback={setFormFields}
            currentLayerId={currentLayerId}
            fieldDropdown={fieldDropdown}
          ></AttributeQueryBlock>
        </div>
      );
    });
  }
  let style = { display: "initial" };
  if (visible) {
    style.display = "initial";
  } else {
    style.display = "none";
  }

  return (
    <>
      <form ref={selectFormWidgetDiv} style={style} id="SelectFormWidget">
        <Typography align="center" variant={"h6"}>
          Build a query for selection
        </Typography>
        <div id="queryDiv" className="esri">
          {layerNameList.length && (
            <Stack
              key={`select-query-${currentLayerId}`}
              direction="column"
              sx={{ mt: 2,mb:2 }}
            >
              <FormControl variant="standard" fullWidth>
                <InputLabel id="select-layer-for-query-label">
                  Select a layer to query
                </InputLabel>
                <Select
                  id="select-layer-for-query"
                  labelId="select-layer-for-query-label"
                  value={currentLayerId}
                  onChange={(e) => {
                    setCurrentLayerId(e.target.value);

                    let layerList = view.map.allLayers.filter((layer) => {
                      return layer.id === e.target.value;
                    });
                    if (layerList.length > 0) {
                      let layer = layerList.getItemAt(0);
                      if (!layer.visible) {
                        layer.set("visible", true);
                      }
                    }
                  }}
                >
                  <MenuItem value={defaultAllVisibleLayers}>
                  {defaultAllVisibleLayers}
                  </MenuItem>
                  {layerDropdown}
                </Select>
              </FormControl>
            </Stack>
          )}
          <ButtonGroup fullWidth>
            <Button
            size='small'
            sx={{fontSize:10, height:"40px"}}
              variant="outlined"
              title="add spatial query"
              disabled={showSpatialQueryBlock}
              onClick={(e) => {
                e.preventDefault();
                setShowSpatialQueryBlock(true);
              }}
            >
              + spatial query
            </Button>
            <Button
            size='small'
            disabled={currentLayerId.length===0 || currentLayerId!==defaultAllVisibleLayers?false:true}
            sx={{fontSize:10, height:"40px"}}
              variant="contained"
              title="add query block"
              onClick={(e) => {
                e.preventDefault();
                for (let prop in formFields) {
                  if (!formFields[prop].show) {
                    let newObject = JSON.parse(
                      JSON.stringify(formFields[prop])
                    );
                    newObject.show = !newObject.show;
                    setFormFields((prev) => ({ ...prev, [prop]: newObject }));
                    setAttributeQueryBlocks((prevState) => [
                      ...prevState,
                      newObject.fieldName,
                    ]);
                    break;
                  }
                }
              }}
            >
              + attribute query
            </Button>
          </ButtonGroup>

          <SpatialSelectWidget
            showWidget={showSpatialQueryBlock}
            setShowWidget={setShowSpatialQueryBlock}
            view={view}
            activeWidget={"spatial-select"}
            attributeObject={attributeObject}
            setDisable={setDisable}
          />

          {attributeQueries}

          <SelectionFormButtons
            view={view}
            attributeObject={attributeObject}
            portal={portal}
            callback={() => resetTabArray()}
          />
        </div>
        {payload.length ? (
          <>
            {/* <SelectCustomToolbar view={view}></SelectCustomToolbar> */}
            <div style={{paddingLeft:'10px'}}>
            <Typography variant="h6" sx={{paddingBottom:2}}>Export Selection</Typography>
              <ExportLayersWidget
                view={view}
                activeWidget={showExportLayersWidget}
              />
            </div>
          </>
        ) : (
          ""
        )}
      </form>
    </>
  );
};

export default SelectFormWidget;
